import { ReactNode, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Button,
  MenuButton,
  MenuItem,
  MenuItemDivider,
  TextArea,
} from "@carbon/react";
import ConfirmDialog from "../../../components/carbon/ConfirmDialog/ConfirmDialog";
import { useNotification, useUser } from "../../../context";
import {
  isNoApprovalRule,
  isOcConflict,
  isOcForbidden,
  useApproveOc,
  useCancelOc,
  useRemindApprover,
  useResendOc,
  useSendOc,
} from "../../../hooks/useOcActions";
import { PurchaseOrderResponse } from "../../../types";
import { ABILITIES } from "../../../utils/abilities";
import EditOcModal from "./EditOcModal";
import RejectOcModal from "./RejectOcModal";
import "./ocDetail.scss";

export interface OcDetailActions {
  /** The primary button + the "Acciones" overflow, for the dialog's footer (null when none apply). */
  footerActions: ReactNode;
  /**
   * The confirm / edit / reject dialogs the actions open. They are modals themselves, so they MUST
   * be rendered as SIBLINGS of the detail dialog (after it, in DOM order), never inside it: a
   * Carbon modal's container is transformed, which would turn an inner `position: fixed` modal
   * into one confined to the outer container. DOM order after the detail dialog also makes them
   * the "topmost" modal, so Escape closes only the inner one.
   */
  dialogs: ReactNode;
}

/**
 * The buyer's status- and ability-gated actions for the OC detail dialog, ported from the detail
 * page. DRAFT -> Editar + Enviar, SENT -> Reenviar + Coordinar entrega, ACCEPTED -> Coordinar
 * entrega, any non-terminal OC -> Anular (with a required reason) — all CREATE_PURCHASE_ORDERS;
 * PENDING_APPROVAL -> Recordar aprobación (APPROVE_PURCHASE_ORDERS), plus Aprobar + Rechazar when
 * the server says this user may approve THIS OC (`oc.canApprove`; the ability is only a fallback
 * while the flag is absent). Coordinar entrega navigates to the coordinate page (it is a full
 * page, not a modal) and closes the dialog.
 */
export const useOcDetailActions = (
  oc: PurchaseOrderResponse | undefined,
  onClose: () => void
): OcDetailActions => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { can } = useUser();
  const { openNotification } = useNotification();
  const [editOpen, setEditOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [resendConfirmOpen, setResendConfirmOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const sendOc = useSendOc();
  const resendOc = useResendOc();
  const remindApprover = useRemindApprover();
  const approveOc = useApproveOc();
  const cancelOc = useCancelOc();

  if (!oc) return { footerActions: null, dialogs: null };

  const canCreate = can(ABILITIES.CREATE_PURCHASE_ORDERS);
  const canApprove = can(ABILITIES.APPROVE_PURCHASE_ORDERS);
  const busy =
    sendOc.isPending ||
    resendOc.isPending ||
    remindApprover.isPending ||
    approveOc.isPending;
  // The server's per-OC verdict is authoritative (a non-designated approver must not see the
  // buttons even holding the ability); the ability is only a fallback while the flag is absent.
  const showApproveReject =
    oc.status === "PENDING_APPROVAL" && (oc.canApprove ?? canApprove);
  const isDraft = oc.status === "DRAFT";
  // ACCEPTED coordinates the delivery; SENT proposes it (the supplier confirms by accepting). The
  // backend answers 409 for any other status.
  const showCoordinate =
    (oc.status === "ACCEPTED" || oc.status === "SENT") && canCreate;
  // Anular: any non-terminal OC. The server enforces the same rule (409 on DELIVERED/CANCELLED).
  const showCancel =
    oc.status !== "DELIVERED" && oc.status !== "CANCELLED" && canCreate;

  const run = (mutation: typeof sendOc, successKey: string, errorKey: string) =>
    mutation.mutate(oc.id, {
      onSuccess: () => openNotification(t(successKey)),
      onError: () => openNotification(t(errorKey)),
    });

  // Enviar: a 409 "no approval rule for this currency" gets its own actionable message.
  const send = () =>
    sendOc.mutate(oc.id, {
      onSuccess: () =>
        openNotification(t("purchaseOrders.actions.sendSuccess")),
      onError: (error) =>
        openNotification(
          t(
            isNoApprovalRule(error)
              ? "purchaseOrders.actions.sendNoApprovalRule"
              : "purchaseOrders.actions.sendError"
          )
        ),
    });

  const approve = () =>
    approveOc.mutate(oc.id, {
      onSuccess: () => openNotification(t("purchaseOrders.approve.success")),
      onError: (error) =>
        openNotification(
          t(
            isOcForbidden(error)
              ? "purchaseOrders.approve.notApprover"
              : isOcConflict(error)
                ? "purchaseOrders.approve.notPending"
                : "purchaseOrders.approve.error"
          )
        ),
    });

  // Who the resend goes to: the supplier contact's email, else a name, else a generic phrase.
  const recipient =
    oc.supplier?.email ||
    oc.supplier?.name ||
    oc.supplier?.companyName ||
    t("purchaseOrders.resend.unknownRecipient");

  const closeCancel = () => {
    setCancelOpen(false);
    setCancelReason("");
  };
  const cancel = () => {
    const reason = cancelReason.trim();
    if (!reason) return;
    cancelOc.mutate(
      { id: oc.id, reason },
      {
        onSuccess: () => {
          openNotification(t("purchaseOrders.actions.cancelSuccess"));
          closeCancel();
        },
        onError: (error) => {
          openNotification(
            t(
              isOcConflict(error)
                ? "purchaseOrders.actions.cancelConflict"
                : "purchaseOrders.actions.cancelError"
            )
          );
          // A 409 means the OC moved on (refetched on settle): the dialog is moot.
          if (isOcConflict(error)) closeCancel();
        },
      }
    );
  };

  const coordinate = () => {
    navigate(`/purchase-orders/coordinate?ids=${oc.id}`);
    onClose();
  };

  // The one contextual PRIMARY action by status; everything else (edit, resend, remind, reject,
  // cancel) collapses into a single "Acciones" menu so the footer never spreads a row of buttons.
  const primaryAction = showApproveReject ? (
    <Button disabled={busy} onClick={approve}>
      {t("purchaseOrders.approve.action")}
    </Button>
  ) : isDraft && canCreate ? (
    <Button disabled={busy} onClick={send}>
      {t("purchaseOrders.actions.send")}
    </Button>
  ) : showCoordinate ? (
    <Button onClick={coordinate}>
      {t("purchaseOrders.coordinate.action")}
    </Button>
  ) : null;

  const menuItems: ReactNode[] = [];
  if (isDraft && canCreate) {
    menuItems.push(
      <MenuItem
        key="edit"
        label={t("purchaseOrders.actions.edit")}
        onClick={() => setEditOpen(true)}
      />
    );
  }
  if (oc.status === "SENT" && canCreate) {
    menuItems.push(
      <MenuItem
        key="resend"
        label={t("purchaseOrders.actions.resend")}
        disabled={busy}
        onClick={() => setResendConfirmOpen(true)}
      />
    );
  }
  if (oc.status === "PENDING_APPROVAL" && canApprove) {
    menuItems.push(
      <MenuItem
        key="remind"
        label={t("purchaseOrders.actions.remind")}
        disabled={busy}
        onClick={() =>
          run(
            remindApprover,
            "purchaseOrders.actions.remindSuccess",
            "purchaseOrders.actions.remindError"
          )
        }
      />
    );
  }
  const dangerItems: ReactNode[] = [];
  if (showApproveReject) {
    dangerItems.push(
      <MenuItem
        key="reject"
        kind="danger"
        label={t("purchaseOrders.reject.action")}
        onClick={() => setRejectOpen(true)}
      />
    );
  }
  if (showCancel) {
    dangerItems.push(
      <MenuItem
        key="cancel"
        kind="danger"
        label={t("purchaseOrders.actions.cancel")}
        disabled={busy || cancelOc.isPending}
        onClick={() => setCancelOpen(true)}
      />
    );
  }
  const hasMenu = menuItems.length + dangerItems.length > 0;

  const footerActions =
    primaryAction || hasMenu ? (
      <>
        {hasMenu ? (
          <MenuButton kind="tertiary" label={t("cta.actions")}>
            {menuItems}
            {menuItems.length > 0 && dangerItems.length > 0 ? (
              <MenuItemDivider />
            ) : null}
            {dangerItems}
          </MenuButton>
        ) : null}
        {primaryAction}
      </>
    ) : null;

  const dialogs = (
    <>
      {isDraft && canCreate ? (
        <EditOcModal
          open={editOpen}
          onClose={() => setEditOpen(false)}
          oc={oc}
        />
      ) : null}

      {oc.status === "SENT" && canCreate ? (
        <ConfirmDialog
          open={resendConfirmOpen}
          title={t("purchaseOrders.resend.confirmTitle")}
          body={t("purchaseOrders.resend.confirmBody", {
            code: oc.code,
            email: recipient,
          })}
          confirmLabel={t("purchaseOrders.resend.confirm")}
          isPending={resendOc.isPending}
          onCancel={() => setResendConfirmOpen(false)}
          onConfirm={() => {
            run(
              resendOc,
              "purchaseOrders.actions.resendSuccess",
              "purchaseOrders.actions.resendError"
            );
            setResendConfirmOpen(false);
          }}
        />
      ) : null}

      {showCancel ? (
        <ConfirmDialog
          open={cancelOpen}
          danger
          title={t("purchaseOrders.cancel.title")}
          body={
            <div className="oc-detail__cancel-body">
              <p>{t("purchaseOrders.cancel.body", { code: oc.code })}</p>
              <TextArea
                id="cancel-oc-reason"
                labelText={t("purchaseOrders.cancel.reason")}
                rows={3}
                value={cancelReason}
                onChange={(event) => setCancelReason(event.target.value)}
              />
            </div>
          }
          confirmLabel={t("purchaseOrders.cancel.confirm")}
          confirmDisabled={cancelReason.trim() === ""}
          isPending={cancelOc.isPending}
          onCancel={closeCancel}
          onConfirm={cancel}
        />
      ) : null}

      {showApproveReject ? (
        <RejectOcModal
          open={rejectOpen}
          onClose={() => setRejectOpen(false)}
          ocId={oc.id}
        />
      ) : null}
    </>
  );

  return { footerActions, dialogs };
};
